import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import ReviewDropdown from "../components/ReviewDropdown";
import ReviewStatusCard from "../components/ReviewStatusCard";
import SecurityReviewForm from "../components/SecurityReviewForm";
import useVendorStore from "../../store/useVendorStore";
import { SECURITY_REVIEW_STATUS } from "../../utils/constants";
import { styles } from "../../styles/Vendors.styles";

const sortedReviews = (reviews = []) =>
  [...reviews].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

const pickDefaultReviewId = (reviews = []) => {
  const draft = reviews.find((r) => r.status === SECURITY_REVIEW_STATUS.DRAFT);
  if (draft) return draft.id;
  return sortedReviews(reviews)[0]?.id || "";
};

const AssessmentTab = ({ vendor, people = [] }) => {
  const theme = useTheme();
  const startReview = useVendorStore((s) => s.startReview);
  const completeReview = useVendorStore((s) => s.completeReview);
  const discardReview = useVendorStore((s) => s.discardReview);
  const hydrateReviewFiles = useVendorStore((s) => s.hydrateReviewFiles);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [selectedReviewId, setSelectedReviewId] = useState("");

  const reviews = useMemo(() => sortedReviews(vendor.reviews || []), [vendor.reviews]);
  const draftReview = reviews.find((r) => r.status === SECURITY_REVIEW_STATUS.DRAFT);
  const selectedReview =
    reviews.find((r) => r.id === selectedReviewId) || reviews[0] || null;

  useEffect(() => {
    setSelectedReviewId(pickDefaultReviewId(vendor.reviews || []));
  }, [vendor.id]);

  useEffect(() => {
    if (selectedReview?.status === SECURITY_REVIEW_STATUS.DRAFT) {
      hydrateReviewFiles(vendor.id, selectedReview.id);
    }
  }, [vendor.id, selectedReview?.id, selectedReview?.status, hydrateReviewFiles]);

  const handleStart = () => {
    if (draftReview) {
      setSelectedReviewId(draftReview.id);
      return;
    }
    const createdId = startReview(vendor.id);
    if (createdId) setSelectedReviewId(createdId);
    setSnackbarInfo({ message: "Security review started" });
  };

  const handleComplete = () => {
    if (!selectedReview) return;
    completeReview(vendor.id, selectedReview.id);
    setSnackbarInfo({ message: `Review completed - ${selectedReview.recommendation}` });
  };

  const handleDiscard = () => {
    if (!selectedReview) return;
    discardReview(vendor.id, selectedReview.id);
    setSnackbarInfo({ message: "Security review discarded" });
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />

      <Card sx={styles.mainCard} variant="outlined">
        <CardHeader
          title={
            <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong}>
              Security Reviews
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1} sx={{ pt: 0.5, pr: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
                onClick={handleStart}
                sx={styles.addButton}
              >
                {draftReview ? "Continue Review" : "Start Review"}
              </Button>
            </Stack>
          }
          sx={styles.cardHeader}
        />
        <CardContent sx={{ p: 3 }}>
          {reviews.length === 0 ? (
            <Box sx={{ ...styles.emptyState, minHeight: 280 }}>
              <Typography variant="h5" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                No Security Reviews Yet
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 420, lineHeight: 1.5 }}>
                Start a security review to evaluate {vendor.name}, upload supporting documents, and record a recommendation.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.25}>
              <ReviewDropdown
                reviews={reviews}
                selectedReviewId={selectedReview?.id}
                onReviewChange={setSelectedReviewId}
                vendorName={vendor.name}
              />
              <ReviewStatusCard review={selectedReview} vendor={vendor} people={people} />
              {selectedReview && (
                <SecurityReviewForm
                  vendor={vendor}
                  review={selectedReview}
                  readOnly={selectedReview.status !== SECURITY_REVIEW_STATUS.DRAFT}
                  onComplete={handleComplete}
                  onDiscard={handleDiscard}
                  onSnackbar={(message) => setSnackbarInfo({ message })}
                />
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default AssessmentTab;
